<template>
  <!-- Phones: the same choices in a bottom sheet (docs/page-layout.md). Not a menu its page opens
       itself (customActivation): it suggests under a field being typed in, which a sheet would hide. -->
  <div v-if="inSheet" class="base-menu-sheet" @click.stop>
    <div class="base-menu__activator" @click="openSheet">
      <slot name="activator">activator</slot>
    </div>
    <!-- As the phone lists' sheets pick a status, labels or assignees (docs/page-layout.md). -->
    <BaseBottomSheet :value="menu" :title="title" @input="onSheetInput">
      <slot v-if="!sheetChoices.length" name="no-result">
        <p class="base-sheet-checklist__empty">{{ $t('form-auto-complete.no-results') }}</p>
      </slot>
      <!-- Several choices: picked one by one, applied as the sheet closes, as the menu does. -->
      <BaseSheetChecklist
        v-else-if="isMultiple"
        :key="openings"
        :value="sheetValue"
        :choices="sheetChoices"
        :label="title"
        :search-from="searchAllowed ? undefined : Infinity"
        @input="onSheetPicks"
        @done="onSheetInput(false)"
      >
        <template v-if="$scopedSlots['sheet-choice']" #default="{ choice }">
          <slot name="sheet-choice" :item="choice.item" />
        </template>
      </BaseSheetChecklist>
      <ul v-else class="base-bottom-sheet__menu" role="radiogroup" :aria-label="title">
        <li v-for="choice in sheetChoices" :key="choice.id">
          <button
            type="button"
            role="radio"
            :aria-checked="String(sheetValue.includes(choice.id))"
            :class="{ 'is-active': sheetValue.includes(choice.id) }"
            @click="pickOne(choice.item)"
          >
            <slot name="sheet-choice" :item="choice.item">{{ choice.name }}</slot>
            <BaseIcon v-if="sheetValue.includes(choice.id)" name="check" class="base-bottom-sheet__menu-check" />
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
  </div>
  <v-menu
    v-else
    ref="baseMenu"
    v-model="menu"
    :close-on-content-click="false"
    transition="scale-transition"
    :origin="getTransitionOrigin"
    :content-class="getHorizontalPosition"
    :attach="!floating && attach ? attach : false"
    :offset-y="floating"
    :disabled="isDisabled"
    @click.stop
  >
    <template v-slot:activator="{ on }">
      <div v-on="customActivation ? '' : on" @click="customActivation ? '' : openMenu" class="base-menu__activator">
        <slot name="activator">activator</slot>
      </div>
    </template>
    <div v-closable="clickOutside">
      <BaseMenuSearchBar
        v-if="searchAllowed"
        class="base-menu__search-bar"
        ref="searchBarRef"
        name="searchBarAloha"
        type="text"
        @search="filterItems($event)"
        hasGrayBackground
      ></BaseMenuSearchBar>
      <div @click="optionClick">
        <div v-if="filteredItems.length <= 0">
          <slot name="no-result">
            <div class="base-menu__no-result">
              {{ $t('form-auto-complete.no-results') }}
            </div>
          </slot>
        </div>
        <ul v-else class="base-menu__ul">
          <li
            v-for="(item, index) in filteredItems"
            :key="index"
            class="base-menu__li"
            :class="{
              'is-selected': isSelected(item),
              'is-hover': index === selectedIndex && !isSelected(item),
            }"
            @click="toggleItem(item)"
          >
            <slot :item="item"></slot>
            <BaseIcon v-show="isSelected(item)" name="check" />
          </li>
        </ul>
        <div v-if="allowCreateMissingItem && searchQuery && !hasExactCorrespondence" class="base-menu__create-item">
          <BaseButton isUnderline isSmall isTransparent isSquare @click.native="createMissingItem" has-icon-left>
            <BaseIcon name="plus" />
            <span class="base-menu__create-item--search-query">{{ $t('actions.create') }} {{ searchQuery }}</span>
          </BaseButton>
        </div>
        <div v-if="!isUnclearable && !goNogo" class="base-menu__append">
          <hr />
          <div @click="clear" class="base-menu__append--clear-button">{{ $t('dialog.filters.advanced.clear') }}</div>
        </div>
      </div>
    </div>
  </v-menu>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseMenuSearchBar from '@/components/pdf-viewer/BaseMenuSearchBar';
import BaseSheetChecklist from '@/components/base/BaseSheetChecklist.vue';
import { viewport } from '@/util/viewport';

export default {
  name: 'BaseMenu',
  components: { BaseBottomSheet, BaseButton, BaseIcon, BaseMenuSearchBar, BaseSheetChecklist },
  props: {
    items: {
      type: Array,
    },
    selection: {
      type: Array,
      required: true,
    },
    isMultiple: {
      type: Boolean,
      default: false,
    },
    isUnclearable: {
      type: Boolean,
      default: false,
    },
    goNogo: {
      type: Boolean,
      default: false,
    },
    attach: {
      type: String,
    },
    // Floating: appended to the app root and positioned by Vuetify under its
    // activator, instead of flowing inside `attach`. For an activator inside a
    // box that clips its overflow, like a table scrolling sideways.
    floating: {
      type: Boolean,
      default: false,
    },
    idIsValue: {
      type: Boolean,
      default: false,
    },
    searchAllowed: {
      type: Boolean,
      required: false,
      default: false,
    },
    customSearchFunction: {
      type: Boolean,
      required: false,
      default: false,
    },
    customActivation: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowCreateMissingItem: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchQuery: {
      type: String,
      default: 'false',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    // The sheet's title on phones: what is picked.
    title: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      menu: false,
      filteredItems: [],
      selectedIndex: -1,
      width: null,
      hasExactCorrespondence: false,
      // Each opening starts the sheet's search afresh.
      openings: 0,
    };
  },
  created() {
    this.filteredItems = this.items;
  },
  computed: {
    inSheet() {
      return viewport.size === 'phone' && !this.customActivation;
    },
    // The choices as the sheets list them: a name, and a person's email under it.
    sheetChoices() {
      return (this.items || []).map((item) => ({
        id: this.idIsValue ? item : item.id,
        name: this.idIsValue ? String(item) : item.name,
        detail: item.email,
        item,
      }));
    },
    sheetValue() {
      return this.selection.map((picked) => (this.idIsValue ? picked.value : picked.id));
    },
    getHorizontalPosition() {
      // Floating, Vuetify keeps the menu inside the viewport itself.
      if (this.floating) return 'base-menu base-menu--floating';
      return this.width > window.innerWidth ? 'base-menu left' : 'base-menu';
    },
    getTransitionOrigin() {
      return this.width > window.innerWidth ? 'top center' : 'top left';
    },
    isSearchDirty() {
      return this.$refs.searchBarRef ? this.$refs.searchBarRef.isDirty : false;
    },
  },
  methods: {
    openSheet() {
      if (this.isDisabled) return;
      this.openings += 1;
      this.menu = true;
    },
    // The checklist says what's ticked now; the menu's events say which one changed.
    onSheetPicks(ids) {
      const added = ids.filter((id) => !this.sheetValue.includes(id));
      const changed = added.length ? added[0] : this.sheetValue.find((id) => !ids.includes(id));
      const choice = this.sheetChoices.find(({ id }) => id === changed);
      if (choice) this.toggleItem(choice.item);
    },
    // A single choice closes the sheet; the one already picked changes nothing.
    pickOne(item) {
      if (!this.isSelected(item)) this.toggleItem(item);
      this.closeMenu();
    },
    // Closed by "Terminé", a tap outside, a swipe or back: as the menu by a click outside.
    onSheetInput(open) {
      if (open) this.menu = true;
      else this.clickOutside();
    },
    optionClick() {
      if (!this.isMultiple) {
        this.closeMenu();
      }
    },
    clickOutside() {
      if (this.menu) {
        this.closeMenu();
        this.$emit('click-outside');
      }
    },
    closeMenu() {
      // A sheet has its own search (BaseSheetChecklist).
      if (this.searchAllowed && !this.inSheet) {
        this.$nextTick(() => {
          this.$refs.searchBarRef.removeFocus();
          this.$refs.searchBarRef.resetSearchBar();
        });
      }
      this.menu = false;
    },
    openMenu() {
      this.menu = true;
      this.$emit('click-outside');
      setTimeout(() => {
        if (!this.$refs.baseMenu) return;
        const content = this.$refs.baseMenu.$refs.content;
        if (!content) return;
        const { x } = content.getBoundingClientRect();
        this.width = content.offsetWidth + x;
      }, 100);

      this.focusSearchBar();
    },
    focusSearchBar(attempt = 0) {
      // A sheet doesn't bring up the keyboard over its choices before one is looked for.
      if (!this.searchAllowed || !this.menu || this.inSheet) return;
      const searchBar = this.$refs.searchBarRef;
      if (searchBar && searchBar.focusInput()) return;
      if (attempt < 20) {
        setTimeout(() => this.focusSearchBar(attempt + 1), 50);
      }
    },
    isSelected(item) {
      return this.idIsValue
        ? this.selection.find((i) => i.value === item)
        : this.selection.find((i) => i.id === item.id);
    },
    toggleItem(item) {
      if (this.isSelected(item) && !this.isUnclearable && this.menu) {
        this.$emit('remove-item', item);
      } else if (!this.isMultiple) {
        // TODO: changeSelection
        this.$emit('select-item', item);
      } else {
        this.$emit('add-item', item);
      }
    },
    clear() {
      if (this.selection.length) {
        this.$emit('clear');
      } else {
        this.closeMenu();
      }
    },
    filterItems(type) {
      if (this.customSearchFunction) return this.$emit('custom-search', type);
      this.filteredItems = this.items.filter((item) => item.name.toLowerCase().includes(type.toLowerCase()));
      if (type.trim() === '') {
        this.selectedIndex = -1;
      } else {
        this.selectedIndex = 0;
      }
    },
    handleArrowKeys(event) {
      if (!this.menu) return;
      if (event.key === 'ArrowUp' && this.selectedIndex > 0) {
        event.preventDefault();
        this.selectedIndex--;
      } else if (event.key === 'ArrowDown' && this.selectedIndex < this.filteredItems.length - 1) {
        event.preventDefault();
        this.selectedIndex++;
      } else if (event.key === 'Enter' && this.selectedIndex >= 0 && this.selectedIndex < this.filteredItems.length) {
        this.toggleItem(this.filteredItems[this.selectedIndex]);
      }
    },
    createMissingItem() {
      this.$emit('create-missing-item', this.searchQuery);
    },
  },
  watch: {
    menu() {
      if (this.menu) {
        this.openMenu();
      }
    },
    width() {
      this.getHorizontalPosition;
    },
    items() {
      this.filteredItems = this.items;
      this.hasExactCorrespondence = this.filteredItems.some(
        (item) => item.name && this.searchQuery && item.name.toLowerCase() === this.searchQuery.toLowerCase(),
      );
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleArrowKeys);
    this.selectedIndex = -1;
  },
  destroyed() {
    document.removeEventListener('keydown', this.handleArrowKeys);
  },
};
</script>

<style lang="scss">
.base-menu {
  border-radius: $border-radius-root;
  background: $color-white;
  padding: 16px;
  box-shadow: $shadow-dropdown;
  outline: none;
  margin-top: 40px;
  z-index: 9999 !important;
  min-width: max-content !important;

  // offset-y already places a floating menu under its activator.
  &--floating {
    margin-top: 4px;
  }

  &__ul {
    max-height: 450px;
    overflow-y: auto;
    padding: 2px 2px 16px;

    &::-webkit-scrollbar-track {
      background-color: $color-neutral-pale;
    }

    &::-webkit-scrollbar {
      height: 0;
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: $border-radius-large;
      background-color: $color-grey-pale;
    }
  }

  &__search-bar {
    align-items: center;
    display: flex;
    position: relative;
    width: 100%;
    justify-content: flex-start;
    margin-bottom: 10px;
  }

  &__li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    list-style: none;
    padding: 8px;
    cursor: pointer;
    border-radius: $border-radius-small;
    margin-bottom: 4px;
    width: 100%;
    font-size: 14px;
    color: $color-normal;
    font-weight: normal;
    gap: 4px;

    &--content {
      padding: 4px 4px 4px 0;
    }

    &:hover {
      background: $color-hover;
    }

    &.is-selected {
      color: $color-accent;
      outline: 1px solid $color-accent-300;
      background: $color-accent-100;
    }

    &.is-hover {
      background-color: $color-hover;
    }

    .base-icon {
      color: $color-accent;
    }
  }

  &__append {
    hr {
      border: 1px solid $color-neutral-pale;
    }

    &--clear-button {
      margin-top: 16px;
      border-radius: $border-radius-small !important;
      cursor: pointer;
      padding: 6px;
      text-decoration: underline;
      font-weight: bold !important;
      color: $color-normal !important;
      text-transform: capitalize;
      letter-spacing: 0;
      font-size: 14px;
      transition: all 200ms ease;
      width: min-content;

      &:hover {
        transition: all 200ms ease;
        background-color: $color-hover !important;
      }
    }
  }

  &__no-result {
    font-size: 14px;
    color: $color-neutral;
  }

  &__create-item {
    margin-top: 8px;

    &--search-query {
      text-transform: capitalize;
    }
  }
}

// Phones: the activator where the menu's would be.
.base-menu-sheet {
  display: contents;
}

.left {
  right: 0 !important;
}

// An attached menu ignores Vuetify's coordinates and flows inside its container,
// right under the activator. A floating one keeps them: it is appended to the
// app root, where auto would put it in the top-left corner.
.v-menu__content.base-menu:not(.base-menu--floating) {
  left: auto !important;
  top: auto !important;
}
</style>
